import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Select, notification, Alert } from 'antd';
import InputMoney from '../../../common/InputMoney/InputMoney';
import { formatPrice } from '../../../../utils/format';
import { apiAccountsList } from '../../../../api/accounts';
import { apiCreateTransactionSplit } from '../../../../api/transactions';
import { getSellerID } from '../../../../api/common';

const { Option } = Select;

const SplitModal = ({ 
  visible, 
  onCancel, 
  onSuccess, 
  dataTransaction,
  loading: externalLoading 
}) => {
  const [form] = Form.useForm();
  const [sellerOptions, setSellerOptions] = useState([]);
  const [splitAmount, setSplitAmount] = useState({ price: "0,00" });
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [splitLoading, setSplitLoading] = useState(false);
  const [sellerLoading, setSellerLoading] = useState(false);
  const [distributors, setDistributors] = useState({});

  // Calculate available amount to split
  const availableToSplit = useMemo(() => {
    if (!dataTransaction?.amount || !dataTransaction?.split_rules) return 0;
    
    // Start with the amount after fees (dataTransaction.amount)
    let amountAfterFees = parseFloat(dataTransaction.amount);
    amountAfterFees = amountAfterFees - parseFloat(dataTransaction.acquirer_fee || 0);
    
    // Subtract existing splits
    const existingSplitsTotal = dataTransaction.split_rules.reduce((total, split) => {
      return total + parseFloat(split.amount || 0);
    }, 0);
    
    return amountAfterFees - existingSplitsTotal;
  }, [dataTransaction]);

  // Calculate remaining amount after current split
  const remainingAfterSplit = useMemo(() => {
    const currentSplitValue = parseFloat(splitAmount.price.replace(',', '.')) || 0;
    return availableToSplit - currentSplitValue;
  }, [availableToSplit, splitAmount.price]);

  const fetchOptions = async (value, setOptions, setLoading, apiCall) => {
    setLoading(true);
    try {
      const response = await apiCall({search: value});
      const results = response.results.reduce((acc, item) => {
        acc[item.id] = item.display_name || item.name;
        return acc;
      }, {});
      setDistributors(prev => ({...prev, ...results}));
      setOptions(response.results.map(item => ({
        value: item.id,
        label: item.display_name || item.name
      })));
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownVisibleChange = async (visible, setOptions, setLoading, apiCall) => {
    if (visible) {
      await fetchOptions('', setOptions, setLoading, apiCall);
    }
  };

  const handleSearch = async (value, setOptions, setLoading, apiCall) => {
    await fetchOptions(value, setOptions, setLoading, apiCall);
  };

  const getDisplayName = (id) => distributors[id] || 'Não informado';  useEffect(() => {
    if (visible) {
      // Reset form and state when modal opens
      form.resetFields();
      setSplitAmount({ price: "0,00" });
      setSelectedSeller(null);
    }
  }, [visible, form]);
  const handleSplitConfirm = async () => {
    try {
      setSplitLoading(true);
      await form.validateFields();
      
      // Prepare the payload for the API call
      const numericValue = parseFloat(splitAmount.price.replace(',', '.'));
    // Convert amount to cents (e.g., 0,10 => 10)
    const amountInCents = Math.round(numericValue * 100);
    const payload = {
      recipient: selectedSeller,
      amount: amountInCents.toString(),
      amount_type: "fixed"
    };
      
      // Get the current seller ID and transaction ID
      const sellerId = getSellerID();
      const transactionId = dataTransaction?.id;
      
      if (!sellerId || !transactionId) {
        throw new Error("Missing seller ID or transaction ID");
      }
      
      // Call the API to create the split
      await apiCreateTransactionSplit(sellerId, transactionId, payload);
      
      notification.success({
        message: "Split criado com sucesso!",
        description: `Split de ${formatPrice(numericValue)} criado para o vendedor selecionado.`
      });
      
      handleCancel();
      
      // Call the success callback to refresh parent data
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error("Error creating split:", error);
      
      // Check if it's a 4xx error and show appropriate message
      let errorMessage = "Ocorreu um erro ao tentar criar o split. Tente novamente.";
      
      if (error.status >= 400 && error.status < 500) {
        errorMessage = "Erro na solicitação. Verifique os dados informados e tente novamente.";
      } else if (error.status >= 500) {
        errorMessage = "Erro interno do servidor. Tente novamente mais tarde.";
      }
      
      notification.error({
        message: "Erro ao criar split",
        description: errorMessage
      });
    } finally {
      setSplitLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSplitAmount({ price: "0,00" });
    setSelectedSeller(null);
    onCancel();
  };

  const onSplitAmountChange = (e) => {
    setSplitAmount({ price: e.target.value });
  };

  return (
    <Modal
      title="Criar Split"
      open={visible}
      onOk={handleSplitConfirm}
      onCancel={handleCancel}
      confirmLoading={splitLoading || externalLoading}
      okText="Criar Split"
      cancelText="Cancelar"
      width={500}
    >
      {/* Display available amount */}
      <Alert
        message={`Valor disponível para split: ${formatPrice(availableToSplit)}`}
        type="info"
        style={{ marginBottom: 16 }}
        showIcon
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSplitConfirm}
      >        <Form.Item
          label="Valor do Split"
          name="amount"
          initialValue="0,00"
          rules={[
            { required: true, message: 'Por favor, insira o valor do split.' },
            {
              validator: (_, value) => {
                const numericValue = parseFloat(splitAmount.price.replace(',', '.'));
                if (numericValue <= 0) {
                  return Promise.reject(new Error('O valor deve ser maior que zero.'));
                }
                if (numericValue > availableToSplit) {
                  return Promise.reject(new Error(`O valor não pode ser maior que o valor disponível para split (${formatPrice(availableToSplit)}).`));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <InputMoney
            value={splitAmount?.price || "0,00"}
            onChange={onSplitAmountChange}
            placeholder="R$ 0,00"
            disabled={splitLoading || externalLoading}
          />
        </Form.Item>        <Form.Item
          label="Vendedor"
          name="seller"
          rules={[
            { required: true, message: 'Por favor, selecione um vendedor.' }
          ]}
        >
          <Select
            showSearch
            value={getDisplayName(selectedSeller) || selectedSeller || undefined}
            placeholder="Selecione um vendedor"
            onChange={setSelectedSeller}
            onSearch={(value) => handleSearch(value, setSellerOptions, setSellerLoading, apiAccountsList)}
            onDropdownVisibleChange={(visible) => handleDropdownVisibleChange(visible, setSellerOptions, setSellerLoading, apiAccountsList)}
            notFoundContent={sellerLoading ? "Carregando..." : "Nenhum vendedor encontrado"}
            style={{width: '100%'}}
            filterOption={false}
            loading={sellerLoading}
            disabled={splitLoading || externalLoading}
          >
            {sellerOptions.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>

      {/* Display remaining amount after split */}
      {parseFloat(splitAmount.price.replace(',', '.')) > 0 && (
        <Alert
          message={`Valor restante após este split: ${formatPrice(remainingAfterSplit)}`}
          type={remainingAfterSplit >= 0 ? "success" : "warning"}
          style={{ marginTop: 16 }}
          showIcon
        />
      )}
    </Modal>
  );
};

export default SplitModal;
